/**
 * Every row a query matches, however many — read page by page.
 *
 * WHY. PostgREST answers at most `max_rows` rows per request (1000 on the
 * hosted projects, `supabase/config.toml`), and says nothing when it stops
 * short. The Lots page used to ask for 500 and print "this sale has more than
 * 500 lots" — but a DTA sale is 535 lots, and Jim's rule is that every lot is
 * on the page and live (2026-10-07/08). So: ask for the total once, then read
 * until it is reached.
 *
 * The total is what decides "done", not a short page: a server whose cap is
 * smaller than `pageSize` returns short pages long before the end, and a loop
 * that stopped at the first short page would cut the sale off silently — the
 * exact failure this replaces. Each request starts where the rows so far end,
 * so a smaller cap only means more requests. Without a total (it was not
 * given), a short or empty page ends it.
 *
 * `page(from, to)` is the caller's query with `.range(from, to)` applied, and
 * `{ count: 'exact' }` on the FIRST request (`from === 0`). It must be ORDERED
 * by a unique key, or rows can repeat or go missing between pages.
 */
export interface RowPage<T> {
  data: T[] | null;
  error: { message: string } | null;
  count?: number | null;
}

export async function fetchEveryRow<T>(
  page: (from: number, to: number) => PromiseLike<RowPage<T>>,
  pageSize = 1000,
): Promise<{ rows: T[]; error: string | null }> {
  const rows: T[] = [];
  let total: number | null = null;
  // A ceiling on requests, so a server that misreports its total cannot spin
  // this forever: enough for 10 million rows at the default page size.
  for (let request = 0; request < 10_000; request++) {
    const { data, error, count } = await page(rows.length, rows.length + pageSize - 1);
    if (error !== null) return { rows, error: error.message };
    if (total === null && typeof count === 'number') total = count;
    const got = data ?? [];
    rows.push(...got);
    if (got.length === 0) break;
    if (total !== null ? rows.length >= total : got.length < pageSize) break;
  }
  return { rows, error: null };
}
